/**
 * 매뉴얼 본문 렌더러.
 *
 * 규약(`docs/manual/README.md`)의 확장 문법을 처리한다.
 * - 콜아웃 3종: `> [!TIP]` / `> [!WARNING]` / `> [!TRAP]`
 * - 용어 링크: `[바인딩](term:binding)` — P1은 점선 밑줄 + 용어집 링크까지 (툴팁은 P4)
 * - `composer-example` 코드펜스: 실제 Composer 렌더러로 그리는 라이브 예제 (`ManualExample`)
 * - 이미지: 문서 기준 상대 경로를 번들 URL로 해석 + 캡션 + 클릭 확대 (`manualAssets`)
 */
import { isValidElement, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Markdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';
import { Modal } from '@safetyhub/ui';
import { manualDocDir, resolveManualAsset } from './manualAssets';
import { findDocBySlug, type ManualDoc as ManualDocModel } from './manualContent';
import { splitCallouts, type CalloutVariant } from './manualCallouts';
import { getStale, type ManualStaleEntry } from './manualStale';
import { ComposerExample } from './ManualExample';
import { getManualFeedback, manualFeedbackKey, recordManualFeedback } from './manualFeedback';

/** 용어집은 매뉴얼마다 하나씩 있다 — 매뉴얼 경계를 넘지 않도록 id를 붙여 해석한다. */
const glossaryPathOf = (manual: string) => `/manual/${manual}/getting-started/glossary`;

const CALLOUT_STYLE: Record<
  CalloutVariant,
  { label: string; icon: string; box: string; head: string }
> = {
  TIP: { label: '팁', icon: '💡', box: 'border-sky-300 bg-sky-50', head: 'text-sky-800' },
  WARNING: { label: '주의', icon: '⚠️', box: 'border-amber-300 bg-amber-50', head: 'text-amber-800' },
  TRAP: { label: '함정', icon: '🚨', box: 'border-rose-300 bg-rose-50', head: 'text-rose-800' },
  NOTE: { label: '참고', icon: 'ℹ️', box: 'border-slate-300 bg-slate-50', head: 'text-slate-700' },
};

const LINK_CLASS = 'text-brand-600 underline underline-offset-2 hover:text-brand-800';

function isExternal(href: string): boolean {
  return /^(https?:)?\/\//.test(href) || href.startsWith('mailto:');
}

/** `<pre>` 자식 `<code>`의 언어(`language-*`)를 읽는다. */
function codeLanguageOf(children: ReactNode): string | null {
  const child = Array.isArray(children) ? children[0] : children;
  if (!isValidElement(child)) return null;
  const className = (child.props as { className?: string }).className ?? '';
  const match = /language-([\w-]+)/.exec(className);
  return match ? match[1] : null;
}

/** `<pre>` 자식 `<code>`의 원문 텍스트(펜스 본문)를 모은다. */
function codeTextOf(children: ReactNode): string {
  const child = Array.isArray(children) ? children[0] : children;
  if (!isValidElement(child)) return '';
  const inner = (child.props as { children?: ReactNode }).children;
  const parts = Array.isArray(inner) ? inner : [inner];
  return parts.filter((part) => typeof part === 'string').join('');
}

/**
 * admin 관습에 맞춘 마크다운 요소 매핑 (콜아웃 내부에도 같은 규칙을 적용).
 *
 * 링크(`a`)만 현재 매뉴얼 id에 의존하므로 여기서 빠져 있다 — `markdownComponentsOf()`가 붙인다.
 */
const MARKDOWN_COMPONENTS: Components = {
  // 문서 제목은 프론트매터 title이 담당 — 본문 `#`은 h2로 낮춰 목차 층위를 일관되게 유지.
  h1: ({ children, id }) => (
    <h2 id={id} className="mt-8 mb-3 scroll-mt-4 text-xl font-bold text-gray-900">
      {children}
    </h2>
  ),
  h2: ({ children, id }) => (
    <h2 id={id} className="mt-8 mb-3 scroll-mt-4 text-xl font-bold text-gray-900">
      {children}
    </h2>
  ),
  h3: ({ children, id }) => (
    <h3 id={id} className="mt-6 mb-2 scroll-mt-4 text-base font-semibold text-gray-900">
      {children}
    </h3>
  ),
  p: ({ children }) => <p className="my-3 leading-7 text-gray-800">{children}</p>,
  ul: ({ children }) => (
    <ul className="my-3 list-disc space-y-1 pl-6 text-gray-800">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="my-3 list-decimal space-y-1 pl-6 text-gray-800">{children}</ol>
  ),
  li: ({ children }) => <li className="leading-7">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="my-4 border-l-4 border-gray-300 pl-4 text-gray-600">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-8 border-gray-200" />,
  strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
  // 레퍼런스 층은 표 중심 — 넓은 표가 본문을 밀지 않도록 가로 스크롤 컨테이너로 감싼다.
  table: ({ children }) => (
    <div className="my-4 overflow-x-auto rounded-card border border-gray-200">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
  th: ({ children }) => (
    <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border-b border-gray-100 px-3 py-2 align-top text-gray-800">{children}</td>
  ),
  code: ({ children, className }) => {
    // 펜스 코드는 pre가 감싸므로 여기서는 인라인 코드만 꾸민다.
    if (className?.startsWith('language-')) {
      return <code className={className}>{children}</code>;
    }
    return (
      <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[0.85em] text-gray-800">
        {children}
      </code>
    );
  },
  pre: ({ children }) => {
    if (codeLanguageOf(children) === 'composer-example') {
      return <ComposerExample source={codeTextOf(children)} />;
    }
    return (
      <pre className="my-4 overflow-x-auto rounded-card bg-gray-900 p-4 font-mono text-sm text-gray-100">
        {children}
      </pre>
    );
  },
};

/** 해석하지 못한 이미지 — 조용히 깨진 `<img>`를 내보내지 않고 눈에 띄게 알린다. */
function MissingImage({ src }: { src: string }) {
  const style = CALLOUT_STYLE.WARNING;
  return (
    <div className={`my-4 rounded-card border px-4 py-3 ${style.box}`}>
      <div className={`mb-1 flex items-center gap-1.5 text-sm font-semibold ${style.head}`}>
        <span aria-hidden="true">{style.icon}</span>
        <span>이미지를 찾을 수 없습니다</span>
      </div>
      <p className="text-sm text-gray-800">
        경로를 확인해 주세요: <code className="rounded bg-white/70 px-1.5 py-0.5 font-mono">{src}</code>
      </p>
    </div>
  );
}

/**
 * 본문 이미지 — 캡션(alt) + 클릭 확대.
 *
 * Composer 화면 캡처는 본문 폭에서 글자가 안 읽혀, 확대가 없으면 그림을 넣는 의미가 없다.
 * 확대 셸은 `@safetyhub/ui`의 `Modal`(포탈·포커스 트랩·Esc·스크롤 잠금) — raw 다이얼로그 금지.
 */
function ManualImage({ docDir, src, alt }: { docDir: string; src: string; alt: string }) {
  const [zoomed, setZoomed] = useState(false);
  const url = resolveManualAsset(docDir, src);
  if (!url) return <MissingImage src={src} />;

  const caption = alt.trim();
  return (
    <figure className="my-5">
      <button
        type="button"
        onClick={() => setZoomed(true)}
        title="클릭하면 원본 크기로 봅니다"
        className="block w-full cursor-zoom-in overflow-hidden rounded-card-sm border border-gray-200 bg-white hover:border-gray-400"
      >
        <img src={url} alt={caption} loading="lazy" className="block h-auto w-full max-w-full" />
      </button>
      {caption && <figcaption className="mt-2 text-sm text-gray-600">{caption}</figcaption>}
      <Modal
        open={zoomed}
        onClose={() => setZoomed(false)}
        title={caption || '이미지 크게 보기'}
        size="xl"
        // ⛔ 예외 — **여섯째이고, 줄기가 적는 「5자리」에는 없다.** 줄기 manifest의 4단계 실측은
        // 예외를 **5자리**로 세는데(이유① 패널 스크롤 3 + 이유② 포탈 안 된 드롭다운 2) 이 자리는
        // **그 두 기준에 걸리지 않는다**: panelClassName이 `!max-w-[92vw]`라 **max-width**여서
        // `max-h-full`과 property 충돌이 없고, 본문은 `<img>`뿐이라 떠 있는 UI가 0이다.
        // ⇒ 줄기의 5와 코드의 6이 어긋나 보이면 **어느 쪽도 낡은 것이 아니다** — 줄기는 두 기준의
        // 수이고 이것이 **제3축**이다(2026-10-27 flip 라운드에 발견 · 팀장 승인). 줄기 쪽 서술은
        // 팀장이 캡처 결과와 함께 갱신한다.
        //
        // 제3축 = **본문이 «양축» 스크롤을 소유한다**: 아래 `overflow-auto` 컨테이너에 붙은 ⛔
        // 주석이 그 이유를 적는다(그 자리를 줄 번호로 가리키지 않는다 — 위에 줄이 끼면 조용히
        // 틀리고, 그 컨테이너는 두 벌로 존재하지 않아 옮겨도 함께 옮겨진다) —
        // base의 `BODY_SCROLL_BODY_CLASS`는 `overflow-y-auto`뿐이라 `max-w-none` 이미지의 **가로**
        // 스크롤을 못 준다. flip은 그 div를 치환하지 않으니 가로 스크롤 자체는 보존되지만, 켜면
        // 본문 div에 세로 스크롤이 하나 더 생겨 **뷰포트 높이 ≈513px 아래에서 스크롤바가 둘**이
        // 된다(계산: 본문 가용 `100vh − 81px` ↔ 내용 `78vh + 32px`). 그리고 이 자리는 푸터가 없고
        // 오버레이 닫기가 살아 있어 **패널 스크롤이 옳은 형상**이다(줄기 분류 규칙) — 켜는 것은
        // 멀쩡한 자리를 고치는 것이다. ⚠️ 이 판정의 레이아웃 축은 jsdom이 못 재므로 캡처가 정본 —
        // 그 정본이 `docs/verification/2026-10-27-modal-bodyscroll-flip/` 이고 위 ≈513px 은 **추정이
        // 아니라 실측**이다(1280×480 에서 이 줄만 걷자 스크롤러 1 → 2 · 안쪽 폭 1122 → 1107).
        // ⭐ 새로 생긴 컨테이너가 `hScroll: false` 인 것이 「base 는 양축을 못 준다」를 클래스
        // 문자열이 아니라 **실제 레이아웃으로** 확증한다 — 이 줄을 지우려는 사람은 그 문서를 먼저 읽어라.
        bodyScroll={false}
        panelClassName="!max-w-[92vw]"
      >
        {/* 원본 크기 유지 — 뷰포트를 넘으면 이 컨테이너가 스크롤한다.
            ⛔ 공용 base `Modal`의 `bodyScroll`로 치환하지 말 것 — 그 유틸은 `overflow-y-auto`뿐이라
            (`BODY_SCROLL_BODY_CLASS`) 아래 `max-w-none` 이미지의 **가로 스크롤을 잃는다**. */}
        <div className="max-h-[78vh] overflow-auto">
          <img src={url} alt={caption} className="block max-w-none" />
        </div>
      </Modal>
    </figure>
  );
}

/**
 * 매뉴얼별 컴포넌트 매핑 — 링크(`a`)는 매뉴얼 id에, 이미지(`img`)는 문서 디렉토리에 의존한다.
 * 조합마다 한 번만 만들어 캐시한다(매 렌더 새 객체를 넘기면 react-markdown이 트리를 다시 만든다).
 *
 * ⚠️ 캐시 키에 `docDir`이 빠지면 다른 섹션의 문서가 **남의 디렉토리로 이미지를 해석**한다
 * (같은 파일명이 섹션마다 있으면 조용히 엉뚱한 그림이 뜬다).
 */
const componentsCache = new Map<string, Components>();

function markdownComponentsOf(manual: string, docDir: string): Components {
  const cacheKey = `${manual}\n${docDir}`;
  const cached = componentsCache.get(cacheKey);
  if (cached) return cached;

  const Img: Components['img'] = ({ src, alt }) => (
    <ManualImage docDir={docDir} src={typeof src === 'string' ? src : ''} alt={alt ?? ''} />
  );

  const components: Components = {
    ...MARKDOWN_COMPONENTS,
    img: Img,
    // 이미지만 있는 문단은 `<p>`로 감싸지 않는다 — `<figure>`가 `<p>` 안에 오면 DOM 규약 위반이다.
    p: ({ children }) => {
      const items = (Array.isArray(children) ? children : [children]).filter(
        (child) => !(typeof child === 'string' && child.trim() === ''),
      );
      const onlyImages =
        items.length > 0 && items.every((child) => isValidElement(child) && child.type === Img);
      if (onlyImages) return <>{children}</>;
      return <p className="my-3 leading-7 text-gray-800">{children}</p>;
    },
    a: ({ href, children }) => {
      const target = href ?? '';

      // 용어 링크 — P1은 용어집으로 이동만 (툴팁은 P4)
      if (target.startsWith('term:')) {
        return (
          <Link
            to={glossaryPathOf(manual)}
            title="용어집에서 보기"
            className="text-sky-700 underline decoration-dotted underline-offset-4 hover:text-sky-900"
          >
            {children}
          </Link>
        );
      }

      if (target.startsWith('/manual/')) {
        return (
          <Link to={target} className={LINK_CLASS}>
            {children}
          </Link>
        );
      }

      if (isExternal(target)) {
        return (
          <a href={target} target="_blank" rel="noreferrer" className={LINK_CLASS}>
            {children}
          </a>
        );
      }

      return (
        <a href={target} className={LINK_CLASS}>
          {children}
        </a>
      );
    },
  };

  componentsCache.set(cacheKey, components);
  return components;
}

/** 마크다운 조각 하나를 렌더. */
function MarkdownBlock({
  manual,
  docDir,
  content,
}: {
  manual: string;
  docDir: string;
  content: string;
}) {
  return (
    <Markdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeSlug]}
      components={markdownComponentsOf(manual, docDir)}
    >
      {content}
    </Markdown>
  );
}

function Callout({
  manual,
  docDir,
  variant,
  content,
}: {
  manual: string;
  docDir: string;
  variant: CalloutVariant;
  content: string;
}) {
  const style = CALLOUT_STYLE[variant];
  return (
    <div className={`my-4 rounded-card border px-4 py-3 ${style.box}`}>
      <div className={`mb-1 flex items-center gap-1.5 text-sm font-semibold ${style.head}`}>
        <span aria-hidden="true">{style.icon}</span>
        <span>{style.label}</span>
      </div>
      <div className="text-gray-800 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
        <MarkdownBlock manual={manual} docDir={docDir} content={content} />
      </div>
    </div>
  );
}

/**
 * 본문(콜아웃 포함) 렌더 — 문서와 매뉴얼 표지가 공유한다.
 * 표지도 같은 확장 문법(콜아웃·용어 링크)을 쓸 수 있어야 하므로 한 경로로 모은다.
 *
 * `docDir`은 이 본문이 놓인 레포 상대 디렉토리 — 이미지 상대 경로를 여기 기준으로 해석한다.
 */
export function ManualBody({
  manual,
  docDir,
  body,
}: {
  manual: string;
  docDir: string;
  body: string;
}) {
  const segments = useMemo(() => splitCallouts(body), [body]);
  return (
    <>
      {segments.map((segment, index) =>
        segment.kind === 'callout' ? (
          <Callout
            key={index}
            manual={manual}
            docDir={docDir}
            variant={segment.variant}
            content={segment.content}
          />
        ) : (
          <MarkdownBlock key={index} manual={manual} docDir={docDir} content={segment.content} />
        ),
      )}
    </>
  );
}

function Badge({ children, tone }: { children: ReactNode; tone: string }) {
  return <span className={`rounded px-2 py-0.5 text-xs font-medium ${tone}`}>{children}</span>;
}

/**
 * 드리프트 안내 — **문서가 틀렸다고 단정하지 않는다.**
 *
 * 판정 근거는 git 이력뿐이라(내용 비교 없음) 코드가 바뀌어도 설명은 여전히 맞는 경우가 훨씬 많다.
 * 그래서 "무엇이 언제 바뀌었는지"라는 사실만 보여주고, 판단은 읽는 사람에게 남긴다.
 *
 * ⚠️ **생성물(`generated`)은 문구를 가른다** — 읽는 사람에게 「내용이 맞는지 확인하라」고 해도
 * 할 수 있는 일이 없다(그 문서는 코드에서 다시 만들어진다). 그 경우 참인 사실은
 * 「**목록에서 빠진 항목이 있을 수 있다**」이고, 처방은 관리자의 재생성이다. 종전에는 둘이 같은
 * 문구를 받아, 영구히 닫히지 않는 경고가 「확인이 필요합니다」로 떠 있었다.
 */
function StaleNotice({ stale }: { stale: ManualStaleEntry }) {
  const changed = (
    <>
      이 문서가 설명하는 파일 {stale.files.length}개가 {stale.generated ? '마지막 생성' : '마지막 확인'}(
      {stale.baselineLabel}) 이후 {stale.totalCommits}번 바뀌었습니다 (마지막 변경{' '}
      {stale.lastChangedAt.slice(0, 10)}).
    </>
  );
  return (
    <p className="mt-3 rounded-card border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
      {changed}{' '}
      {stale.generated
        ? '이 문서는 코드에서 자동으로 만들어지므로, 그 뒤에 늘어난 항목이 아직 실려 있지 않을 수 있습니다. 관리자가 다시 생성하면 채워집니다.'
        : '내용이 여전히 맞는지 확인이 필요합니다.'}
    </p>
  );
}

/**
 * 문서 피드백 — 예/아니오 한 번.
 *
 * 응답은 브라우저에만 남는다(`manualFeedback` 주석 참조 — admin은 슈퍼관리자 세션이라 기존
 * 사용자 피드백 API를 쓸 수 없다). 이미 응답한 문서는 질문 대신 감사 문구만 보여준다.
 */
function DocFeedback({ docKey }: { docKey: string }) {
  // docKey가 바뀌면(문서 이동) 저장소를 다시 읽는다 — useState 초기값은 key로 리마운트해 갱신한다.
  const [answered, setAnswered] = useState(() => getManualFeedback(docKey) !== undefined);

  if (answered) {
    return (
      <p className="mt-10 border-t border-gray-200 pt-4 text-sm text-gray-500">
        의견 주셔서 고맙습니다.
      </p>
    );
  }

  const answer = (helpful: boolean) => {
    recordManualFeedback(docKey, helpful);
    setAnswered(true);
  };

  return (
    <div className="mt-10 flex flex-wrap items-center gap-3 border-t border-gray-200 pt-4">
      <span className="text-sm text-gray-700">이 문서가 도움이 되었나요?</span>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => answer(true)}
          className="rounded border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-50"
        >
          예
        </button>
        <button
          type="button"
          onClick={() => answer(false)}
          className="rounded border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-50"
        >
          아니오
        </button>
      </div>
    </div>
  );
}

export default function ManualDoc({ doc }: { doc: ManualDocModel }) {
  const related = doc.related.map((slug) => ({ slug, doc: findDocBySlug(doc.manual, slug) }));
  const stale = getStale(doc.manual, doc.section, doc.slug);
  const docKey = manualFeedbackKey(doc.manual, doc.section, doc.slug);

  return (
    <article className="max-w-3xl">
      <header className="mb-6 border-b border-gray-200 pb-4">
        <h1 className="text-2xl font-bold text-gray-900">{doc.title}</h1>
        {doc.summary && <p className="mt-2 text-gray-600">{doc.summary}</p>}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {doc.level && <Badge tone="bg-indigo-100 text-indigo-800">{doc.level}</Badge>}
          {doc.readingTime !== undefined && (
            <Badge tone="bg-gray-100 text-gray-700">약 {doc.readingTime}분</Badge>
          )}
          {doc.generated && <Badge tone="bg-emerald-100 text-emerald-800">자동 생성됨</Badge>}
          {stale && (
            <Badge tone="bg-amber-100 text-amber-800">
              {stale.generated ? '재생성 대기' : '업데이트 필요'}
            </Badge>
          )}
          {doc.incomplete && (
            <Badge tone="bg-red-100 text-red-800">
              프론트매터 누락: {doc.missingKeys.join(', ')}
            </Badge>
          )}
        </div>
        {stale && <StaleNotice stale={stale} />}
      </header>

      <div className="text-[15px]">
        <ManualBody manual={doc.manual} docDir={manualDocDir(doc.path)} body={doc.body} />
      </div>

      {related.length > 0 && (
        <footer className="mt-10 border-t border-gray-200 pt-4">
          <h2 className="mb-2 text-sm font-semibold text-gray-700">관련 문서</h2>
          <ul className="space-y-1 text-sm">
            {related.map(({ slug, doc: target }) => (
              <li key={slug}>
                {target ? (
                  <Link
                    to={`/manual/${target.manual}/${target.section}/${target.slug}`}
                    className="text-brand-600 hover:text-brand-800 hover:underline"
                  >
                    {target.title}
                  </Link>
                ) : (
                  <span className="text-gray-400" title="아직 작성되지 않은 문서입니다">
                    {slug} (준비 중)
                  </span>
                )}
              </li>
            ))}
          </ul>
        </footer>
      )}

      <DocFeedback key={docKey} docKey={docKey} />
    </article>
  );
}
